import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import ClickOutside from 'vue-click-outside'
import { DatePicker } from 'src/elements/input/date-picker'
import { Button } from '@src/elements/button/button'
import { H6, Subtitle1 } from '@src/elements/typography/typography'
import { StationWorkoutResults } from '@src/data/workout-results'
import { Select } from '@src/elements/select/select'
import { OptionGroup } from '@src/elements/select/option-group'
import { DropdownOption, Option } from '@src/elements/select/option'
import { Icon } from '@src/elements/icon/icon'
import { WorkoutService } from '@src/service/workout'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { convertToYYYYMMDD } from '@src/util/date-format'
import Member from '@src/data/member'
import { TranslateResult } from 'vue-i18n'
import { Spacer } from '@src/elements/spacer/spacer'
import { formatDate } from '@src/util/functions'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { ScoreChart } from '@src/routes/members/components/status/score-chart'

import './status-overview.sass'
import '../../../../components/composition/portrait/index.sass'
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  DEFAULT_PRINT_CHART_HEIGHT,
  DEFAULT_PRINT_CHART_WIDTH,
  CHART_Y_AXIS_OFFSET,
  SCORE_BULLET_COLOR,
  SCORE_TYPES, APEX_CHARTS_TYPES
} from '@src/util/constants'
import { Legend } from '@src/components/legend/legend'
import { getKeyByView } from '@src/util/translation'
import moment from 'moment'
import VueApexCharts from 'vue-apexcharts'

Vue.use(VueApexCharts)

Vue.component('apexchart', VueApexCharts)

type Scores = {
  pixScores: number[],
  tempoScores: number[],
  precisionScores: number[],
  rangeScores: number[]
}

@Component({
  directives: {
    ClickOutside
  },
  computed: {
    message () {
      return this.$store.state.overviewMessage || this.$t('PAGE.MEMBERS.STATUS.STATUS_OVERVIEW_OVERALL')
    },
    locale () {
      return this.$store.state.i18n.locale
    },
    selectedFilters () {
      return this.$store.state.statusScoreChartSelectedFilters
    }
  }
})
export class StatusOverview extends Vue {

  static RPE_PLOT_COLORS = [
    { fillColor: '#ff7f85', y: 1, y2: 3, opacity: 0.5, strokeDashArray: 0 },
    { fillColor: '#ffdb6c', y: 3, y2: 6, opacity: 0.5, strokeDashArray: 0 },
    { fillColor: '#9cf1c9', y: 6, y2: 8, opacity: 0.5, strokeDashArray: 0 },
    { fillColor: '#ff7f85', y: 8, y2: 10, opacity: 0.5, strokeDashArray: 0 }
  ]

  @Prop({ required: false, default: null })
  member: Member

  protected loading: boolean = true

  protected workoutResults: StationWorkoutResults[]

  private selectedPeriod: 'LAST_WORKOUTS' | 'OVERALL' | 'SPECIFIED_TIME_FRAME' = 'LAST_WORKOUTS'
  private startDate: string = ''
  private endDate: string = ''
  private limit: string = ''

  private scores: Scores = {
    pixScores: [],
    tempoScores: [],
    precisionScores: [],
    rangeScores: []
  }
  private scoreCategories: any[] = []
  private rpeScore: number[] = []
  private rpeCategories: any[] = []
  private message: TranslateResult
  private selectedFilters: string[]
  private timeRangeDropdownIsVisible: boolean = false
  private locale: string

  constructor () {
    super()
  }

  getChartOptions (
    categories,
    printMode: boolean,
    yAxisMinValue?: number,
    yAxisMaxValue?: number,
    tickAmount?: number,
    yAxisAnnotations?: any,
    chartType: string = APEX_CHARTS_TYPES.AREA
  ) {

    const options = {
      chart: {
        type: chartType,
        height: printMode ? DEFAULT_PRINT_CHART_HEIGHT : DEFAULT_CHART_HEIGHT,
        width: printMode ? DEFAULT_PRINT_CHART_WIDTH : DEFAULT_CHART_WIDTH,
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        },
        animations: {
          enabled: true
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 1.5,
        curve: 'smooth'
      },
      legend: {
        show: false
      },
      tooltip: {
        showOnMarkerHover: true,
        onDatasetHover: {
          highlightDataSeries: true
        },
        shared: chartType === APEX_CHARTS_TYPES.LINE ? true : false,
        intersect: chartType === APEX_CHARTS_TYPES.LINE || chartType === APEX_CHARTS_TYPES.AREA ? false : true,
        marker: {
          show: true
        }
      },
      yaxis: {
        opposite: true,
        tickAmount: tickAmount,
        min: yAxisMinValue,
        max: yAxisMaxValue ? yAxisMaxValue + CHART_Y_AXIS_OFFSET : undefined,
        labels: {
          color: '#212121',
          fontSize: '0.675rem',
          formatter: (value) => {
            return Math.trunc(value)
          }
        }
      },
      xaxis: {
        type: 'datetime',
        categories: categories,
        tooltip: {
          enabled: false
        },
        labels: {
          style: {
            color: '#212121',
            fontSize: '0.675rem',
            transform: 'rotate(0)'
          }
        }
      },
      annotations: {
        position: 'back',
        yaxis: yAxisAnnotations || []
      }
    }

    if (chartType === APEX_CHARTS_TYPES.SCATTER) {
      return {
        ...options,
        markers: {
          size: 7,
          strokeWidth: 2,
          strokeOpacity: 0.9,
          strokeDashArray: 0,
          shape: 'circle'
        }
      }
    }

    return { ...options, markers: { size: 0, hover: { size: 7 } } }
  }

  setStartDate (value) {
    this.selectedPeriod = 'SPECIFIED_TIME_FRAME'
    this.startDate = value
    this.limit = ''
    return this.loadWorkoutResults()
  }

  setEndDate (value) {
    this.selectedPeriod = 'SPECIFIED_TIME_FRAME'
    this.endDate = value
    this.limit = ''
    if (this.startDate) this.timeRangeDropdownIsVisible = false
    return this.loadWorkoutResults()
  }

  displayLastWorkouts () {
    this.selectedPeriod = 'LAST_WORKOUTS'
    this.startDate = ''
    this.endDate = ''
    this.limit = '10'
    this.timeRangeDropdownIsVisible = false
    return this.loadWorkoutResults()
  }

  displayOverall () {
    // past 6 months
    this.selectedPeriod = 'OVERALL'
    this.startDate = ''
    this.endDate = ''
    this.limit = ''
    this.timeRangeDropdownIsVisible = false
    return this.loadWorkoutResults()
  }

  mounted () {
    return this.displayLastWorkouts()
  }

  printStatus () {
    window.print()
  }

  filter (measurementType) {
    let filters = [ ...this.selectedFilters ]
    let index = filters.indexOf(measurementType)

    if (index >= 0) {
      filters.splice(index, 1)
    } else {
      filters.push(measurementType)
    }

    this.$store.dispatch('updateStatusScoreChartSelectedFilters', filters)
  }

  @Watch('locale')
  async updateTitleOnLanguageChange () {
    await this.setTitleMessage()
    this.setData()
    this.$forceUpdate()
  }

  updateTimeRangeDropdownStateOnPortrait () {
    this.timeRangeDropdownIsVisible = !this.timeRangeDropdownIsVisible
  }

  render (h: CreateElement) {
    let firstAvailableDateFromFormat = 'DD/MM/YYYY'
    let firstAvailableDateToFormat = 'MM/DD/YYYY'
    if (this.locale.includes('ja')) {
      firstAvailableDateFromFormat = 'YYYY/MM/DD'
      firstAvailableDateToFormat = 'YYYY/MM/DD'
    }
    return <div class='status-overview'>
      <div class='row'>
        <div class='row time-range-wrapper'>
          <H6 class='title'>{ this.message }</H6>
          <div class='portrait-btns-wrapper'>
            <Icon id='time-range-icon' name='util/date-range' click={this.updateTimeRangeDropdownStateOnPortrait}/>
            {this.timeRangeDropdownIsVisible && (<div class={'dropdown-wrapper'} v-click-outside={this.updateTimeRangeDropdownStateOnPortrait}>
              <div class={'dropdown'} >
                <OptionGroup extraStyles={ { overflow: 'initial !important' }}>
                  <DropdownOption label={this.$t('PAGE.MEMBERS.STATUS.SPECIFIED_TIME_FRAME')}>
                    <div class='date-options'>
                      <DatePicker ref='startDate'
                                  id='status-picker-start-date'
                                  type='date'
                                  value={this.startDate}
                                  change={this.setStartDate}
                                  label={this.$t('START_DATE')} allowFutureDate={false}
                                  popper-class={ 'over-modal mobile-date-picker-start' }
                                  appendToBody={false}
                                  firstAvailableDate={this.endDate ? formatDate(this.endDate, firstAvailableDateFromFormat, firstAvailableDateToFormat) : new Date() }/>
                      <DatePicker ref='endDate'
                                  id='status-picker-end-date'
                                  type='date'
                                  value={this.endDate}
                                  change={this.setEndDate}
                                  popper-class={ 'over-modal mobile-date-picker-end' }
                                  appendToBody={false}
                                  label={this.$t('END_DATE')} allowPastDate={!this.startDate} allowFutureDate={!this.startDate}
                                  firstAvailableDate={this.startDate ? formatDate(this.startDate, firstAvailableDateFromFormat, firstAvailableDateToFormat) : new Date()}/>

                    </div>
                  </DropdownOption>
                  <Option type='single-option' value={'LAST_WORKOUTS'} label={this.$t('PAGE.MEMBERS.STATUS.LAST_WORKOUTS')}
                          selected={this.selectedPeriod === 'LAST_WORKOUTS'}
                          select={this.displayLastWorkouts} />
                  <Option type='single-option' value={'OVERALL'} label={this.$t('PAGE.MEMBERS.STATUS.OVERALL')}
                          selected={this.selectedPeriod === 'OVERALL'}
                          select={this.displayOverall} />
                </OptionGroup>
              </div>
            </div>)}
          </div>
        </div>
        <div class='print-details'><H6>Pixformance | {this.member.fullName} | {this.message}</H6><Spacer /></div>

        <div class='btns-wrapper'>
          <Select filter={true} placeholder={this.$t('CHOOSE_TIME_PERIOD')} ref='timeFilter'>
            <OptionGroup extraStyles={ { overflow: 'initial !important' }}>
              <DropdownOption label={this.$t('PAGE.MEMBERS.STATUS.SPECIFIED_TIME_FRAME')}>
                <div class='date-options'>
                  <DatePicker ref='startDate' type='date' value={this.startDate} change={this.setStartDate}
                              label={this.$t('START_DATE')} allowFutureDate={false}
                              popper-class={ 'over-modal date-picker-start' }
                              appendToBody={false}
                              firstAvailableDate={this.endDate ? formatDate(this.endDate, firstAvailableDateFromFormat, firstAvailableDateToFormat) : new Date() }/>
                  <DatePicker ref='endDate' type='date' value={this.endDate} change={this.setEndDate}
                              popper-class={ 'over-modal date-picker-end' }
                              appendToBody={false}
                              label={this.$t('END_DATE')} allowPastDate={!this.startDate} allowFutureDate={!this.startDate}
                              firstAvailableDate={this.startDate ? formatDate(this.startDate, firstAvailableDateFromFormat, firstAvailableDateToFormat) : new Date()}/>

                </div>
              </DropdownOption>
              <Option type='single-option' value={'LAST_WORKOUTS'} label={this.$t('PAGE.MEMBERS.STATUS.LAST_WORKOUTS')}
                      selected={this.selectedPeriod === 'LAST_WORKOUTS'}
                      select={this.displayLastWorkouts} />
              <Option type='single-option' value={'OVERALL'} label={this.$t('PAGE.MEMBERS.STATUS.OVERALL')}
                      selected={this.selectedPeriod === 'OVERALL'}
                      select={this.displayOverall} />
            </OptionGroup>
          </Select>

          <Button type='remove' click={this.printStatus} icon={'util/print'} label={this.$t('PRINT')}
                  class='print-btn' />
          <Icon class={'mobile-print-btn'} name={'util/print'} click={this.printStatus} />
        </div>
      </div>
      <div class='portrait-member-status-legend visible-on-mobile'>
        <Legend select={this.filter} items={
          SCORE_TYPES.slice(0, SCORE_TYPES.length / 2).map(item => {
            return {
              type: item,
              label: this.$t(getKeyByView(`PAGE.MEMBERS.TRAINING.HISTORY.${item.toUpperCase()}`)),
              color: SCORE_BULLET_COLOR[item],
              disabled: !this.selectedFilters.includes(item)
            }
          })
        } />
        <Legend select={this.filter} items={
          SCORE_TYPES.slice(SCORE_TYPES.length / 2, SCORE_TYPES.length).map(item => {
            return {
              type: item,
              label: this.$t(getKeyByView(`PAGE.MEMBERS.TRAINING.HISTORY.${item.toUpperCase()}`)),
              color: SCORE_BULLET_COLOR[item],
              disabled: !this.selectedFilters.includes(item)
            }
          })
        } />
      </div>
      <div class='portrait-member-status-legend visible-on-tablet'>
        <Legend select={this.filter} items={
          SCORE_TYPES.map(item => {
            return {
              type: item,
              label: this.$t(getKeyByView(`PAGE.MEMBERS.TRAINING.HISTORY.${item.toUpperCase()}`)),
              color: SCORE_BULLET_COLOR[item],
              disabled: !this.selectedFilters.includes(item)
            }
          })
        } />
      </div>
      {this.loading ? <Loader/> : <div class='charts row'>
        <ScoreChart
          scores={this.scores}
          scoreCategories={this.scoreCategories}
          getChartOptions={this.getChartOptions}
        />
        {this.renderRPEChart(h)}
      </div>}
    </div>
  }

  renderRPEChart (h: CreateElement) {
    return <div class='chart'>
      <Subtitle1>{this.$t('PAGE.MEMBERS.STATUS.RPE_SCORE')}</Subtitle1>
      {this.rpeScore.length ?
        <div>
          <div class='browser-chart'>
            <apexchart
              ref='rpe-browser-chart'
              height={DEFAULT_CHART_HEIGHT}
              width={DEFAULT_CHART_WIDTH}
              options={this.getChartOptions(
                this.rpeCategories,
                false,
                1
                ,10,
                9,
                StatusOverview.RPE_PLOT_COLORS,
                this.rpeScore.length === 1 ? APEX_CHARTS_TYPES.SCATTER : APEX_CHARTS_TYPES.LINE)}
              series={this.getRpeSeries()} />
          </div>
          <div class='print-chart'>
            <apexchart
              ref='rpe-print-chart'
              height={DEFAULT_PRINT_CHART_HEIGHT}
              width={DEFAULT_PRINT_CHART_WIDTH}
              options={this.getChartOptions(
                this.rpeCategories,
                true,
                1
                ,10,
                9,
                StatusOverview.RPE_PLOT_COLORS,
                this.rpeScore.length === 1 ? APEX_CHARTS_TYPES.SCATTER : APEX_CHARTS_TYPES.LINE)}
              series={this.getRpeSeries()} />
          </div>
        </div>
        : <ErrorScreen icon='brand/empty-graph' message={this.$t('BODY_MEASUREMENTS.EMPTY_GRAPH.RPE_SCORE')} />}
    </div>
  }

  getRpeSeries () {
    if (this.rpeScore.length === 1) {
      return [ {
        data: [
          { x: new Date(this.rpeCategories[0]).getTime(), y: this.rpeScore[0] }
        ],
        color: '#000000',
        name: this.$t('PAGE.MEMBERS.STATUS.RPE_SCORE')
      }]
    }
    return [ { data: this.rpeScore, color: '#000000', name: this.$t('PAGE.MEMBERS.STATUS.RPE_SCORE'), line: APEX_CHARTS_TYPES.LINE } ]
  }

  protected loadWorkoutResults () {
    this.setTitleMessage()
    let limit
    let startDate = ''
    let endDate = ''
    if (this.limit !== '') {
      limit = this.limit
      return this.getResults(this.$route.params.id, startDate, endDate, limit)
    }
    startDate = convertToYYYYMMDD(this.startDate)
    endDate = convertToYYYYMMDD(this.endDate)
    // really old date as default start
    if (!this.startDate) { startDate = '1990-01-01' }
    // today as default date
    if (!this.endDate) endDate = convertToYYYYMMDD(new Date())

    return this.getResults(this.$route.params.id, startDate, endDate, limit)
  }

  protected getResults (memberId, startDate, endDate, limit) {
    return new WorkoutService().getWorkoutResults(memberId, 1, limit ? limit : 158, startDate, endDate)
      .then((result) => {
        this.workoutResults = result.workoutResults
        this.setData()
      })
      .finally(() => this.loading = false)
  }

  protected setData () {
    let rpeScore: number[] = []
    let rpeCategories: any[] = []

    let pixScores: number[] = []
    let tempoScores: number[] = []
    let precisionScores: number[] = []
    let rangeScores: number[] = []
    let scoreCategories: any[] = []

    this.workoutResults
      .sort((a, b) => {
        return a.startedAt > b.startedAt ? 1 : a.startedAt < b.startedAt ? -1 : 0
      })
      .forEach(result => {
        moment.locale(this.locale.split('-')[0])

        pixScores.push(result.pixformanceScore === 0 ? CHART_Y_AXIS_OFFSET : result.pixformanceScore)
        tempoScores.push(result.tempoScore === 0 ? CHART_Y_AXIS_OFFSET : result.tempoScore)
        precisionScores.push(result.precisionScore === 0 ? CHART_Y_AXIS_OFFSET : result.precisionScore)
        rangeScores.push(result.rangeScore === 0 ? CHART_Y_AXIS_OFFSET : result.rangeScore)
        scoreCategories.push(result.startedAt)

        if (result.intensityRating > 0) {
          rpeScore.push(result.intensityRating + CHART_Y_AXIS_OFFSET)
          rpeCategories.push(result.startedAt)
        }
      })

    this.scores = { pixScores, tempoScores, precisionScores, rangeScores }
    this.scoreCategories = scoreCategories
    this.rpeScore = rpeScore
    this.rpeCategories = rpeCategories
  }

  private setTitleMessage () {
    let message
    switch (this.selectedPeriod) {
      case 'LAST_WORKOUTS':
        message = this.$t('PAGE.MEMBERS.STATUS.STATUS_OVERVIEW', { workouts: 10 })
        break
      case 'SPECIFIED_TIME_FRAME':
        message = (this.startDate !== '' && this.endDate !== '' && this.endDate >= this.startDate) ? this.$t('PAGE.MEMBERS.STATUS.STATUS_OVERVIEW_PERIOD', { period: `${this.startDate}-${this.endDate}` }) : ''
        break
      default:
        message = this.$t('PAGE.MEMBERS.STATUS.STATUS_OVERVIEW_OVERALL')
    }
    return this.$store.dispatch('setOverviewMessage', message)
  }
}
